import React, { useState } from 'react'
import { FlaskConical, Clock, Plus, Check, Undo2, FileText } from 'lucide-react'
import { router, Link } from '@inertiajs/react'
import DashboardLayout from '../layouts/DashboardLayout'
import { cn } from '../lib/utils'

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const today = () => new Date().toISOString().slice(0, 10)

// Patch a single field on a lab case (used by the inline editors).
const patchCase = (id, payload) => router.patch(`/lab_cases/${id}`, payload, { preserveScroll: true })

function OutRow({ c }) {
  const [lab, setLab] = useState(c.lab_name || '')
  const [due, setDue] = useState(c.due_on ? c.due_on.slice(0, 10) : '')

  return (
    <tr className={cn(c.overdue && 'bg-red-50/60')}>
      <td className="px-3 py-2 font-medium text-brand-ink">
        {c.patient_id
          ? <Link href={`/patients/${c.patient_id}`} className="hover:underline">{c.patient_name || '—'}</Link>
          : (c.patient_name || '—')}
        {c.invoice_id && (
          <Link href={`/invoices/${c.invoice_id}`} className="ml-1 inline-flex items-center text-brand-muted hover:text-brand-ink" title="View invoice">
            <FileText size={12} />
          </Link>
        )}
      </td>
      <td className="px-3 py-2 text-brand-muted">
        {c.description || '—'}{c.tooth ? ` · #${c.tooth}` : ''}
        {c.codes && <span className="ml-1 text-[11px] text-brand-muted/70">({c.codes})</span>}
      </td>
      <td className="px-3 py-2">
        <input
          value={lab}
          placeholder="Which lab?"
          onChange={(e) => setLab(e.target.value)}
          onBlur={() => { if (lab !== (c.lab_name || '')) patchCase(c.id, { lab_name: lab }) }}
          className="w-32 rounded-md border border-brand-border bg-white px-2 py-1 text-xs focus:border-brand-primary focus:outline-none"
        />
      </td>
      <td className="px-3 py-2 text-brand-muted">{fmtDate(c.sent_on)}</td>
      <td className="px-3 py-2">
        <input
          type="date"
          value={due}
          onChange={(e) => { setDue(e.target.value); patchCase(c.id, { due_on: e.target.value }) }}
          className={cn('rounded-md border bg-white px-2 py-1 text-xs focus:outline-none',
            c.overdue ? 'border-brand-danger text-brand-danger' : 'border-brand-border')}
        />
        {c.overdue && <div className="mt-0.5 inline-flex items-center gap-0.5 text-[11px] text-brand-danger"><Clock size={11} /> overdue</div>}
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <button type="button" onClick={() => patchCase(c.id, { status: 'returned' })}
          className="rounded-lg border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 transition hover:bg-emerald-100">
          Mark returned
        </button>
      </td>
    </tr>
  )
}

function ReturnedRow({ c }) {
  return (
    <tr>
      <td className="px-3 py-2 font-medium text-brand-ink">
        {c.patient_id
          ? <Link href={`/patients/${c.patient_id}`} className="hover:underline">{c.patient_name || '—'}</Link>
          : (c.patient_name || '—')}
      </td>
      <td className="px-3 py-2 text-brand-muted">{c.description || '—'}{c.tooth ? ` · #${c.tooth}` : ''}</td>
      <td className="px-3 py-2 text-brand-muted">{c.lab_name || '—'}</td>
      <td className="px-3 py-2 text-emerald-600">
        {c.status === 'seated' ? `seated ${fmtDate(c.seated_on)}` : `returned ${fmtDate(c.returned_on)}`}
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {c.status === 'returned'
          ? <>
              <button type="button" onClick={() => patchCase(c.id, { status: 'seated' })}
                className="rounded-lg border border-indigo-300 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 transition hover:bg-indigo-100">
                <Check size={12} className="mr-1 inline" />Close out (seated)
              </button>
              <button type="button" onClick={() => patchCase(c.id, { status: 'out' })}
                className="ml-1 rounded-lg border border-brand-border bg-white px-2 py-1 text-xs text-brand-muted transition hover:bg-brand-surface" title="Re-open (still out)">
                <Undo2 size={12} />
              </button>
            </>
          : <span className="text-[11px] text-brand-muted">closed</span>}
      </td>
    </tr>
  )
}

export default function Lab({ out_at_lab = [], returned = [], stats = {} }) {
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState({ description: '', lab_name: '', tooth_numbers: '', due_on: '' })

  const submitManual = (e) => {
    e.preventDefault()
    router.post('/lab_cases', form, {
      preserveScroll: true,
      onSuccess: () => { setForm({ description: '', lab_name: '', tooth_numbers: '', due_on: '' }); setShowAdd(false) },
    })
  }

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary"><FlaskConical size={18} className="text-white" /></div>
          <div>
            <h1 className="text-xl font-semibold text-brand-ink">Lab cases</h1>
            <p className="text-sm text-brand-muted">{stats.out ?? 0} out at the lab — cases open automatically when lab work is invoiced. Set the lab, chase overdue ones, and close them out when seated.</p>
          </div>
        </div>
        <button type="button" onClick={() => setShowAdd((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border bg-white px-3 py-2 text-sm font-medium text-brand-ink transition hover:bg-brand-surface">
          <Plus size={15} /> Add case
        </button>
      </div>

      {showAdd && (
        <form onSubmit={submitManual} className="mb-5 grid grid-cols-1 gap-3 rounded-xl border border-brand-border bg-white p-4 sm:grid-cols-5">
          <input required placeholder="Description (e.g. PFM crown)" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="rounded-md border border-brand-border px-2 py-1.5 text-sm focus:border-brand-primary focus:outline-none sm:col-span-2" />
          <input placeholder="Tooth (e.g. 14)" value={form.tooth_numbers}
            onChange={(e) => setForm({ ...form, tooth_numbers: e.target.value })}
            className="rounded-md border border-brand-border px-2 py-1.5 text-sm focus:border-brand-primary focus:outline-none" />
          <input placeholder="Lab name" value={form.lab_name}
            onChange={(e) => setForm({ ...form, lab_name: e.target.value })}
            className="rounded-md border border-brand-border px-2 py-1.5 text-sm focus:border-brand-primary focus:outline-none" />
          <div className="flex gap-2">
            <input type="date" value={form.due_on} title="Due back"
              onChange={(e) => setForm({ ...form, due_on: e.target.value })}
              className="w-full rounded-md border border-brand-border px-2 py-1.5 text-sm focus:border-brand-primary focus:outline-none" />
            <button type="submit" className="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-white">Add</button>
          </div>
        </form>
      )}

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {[
          { label: 'Out at lab', value: stats.out ?? 0, tone: 'text-brand-ink' },
          { label: 'Overdue', value: stats.overdue ?? 0, tone: 'text-brand-danger' },
          { label: 'Returned (30d)', value: stats.returned_30d ?? 0, tone: 'text-emerald-600' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-brand-border bg-white px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">{s.label}</p>
            <p className={cn('mt-0.5 text-2xl font-bold', s.tone)}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Out at the lab */}
      <h2 className="mb-2 text-sm font-semibold text-brand-ink">Out at the lab</h2>
      {out_at_lab.length === 0 ? (
        <div className="mb-8 rounded-xl border border-dashed border-brand-border bg-white px-6 py-10 text-center text-sm text-brand-muted">
          Nothing is out at the lab right now. Cases open automatically when a crown, bridge, denture or lab (8099) line is invoiced.
        </div>
      ) : (
        <div className="mb-8 overflow-x-auto rounded-xl border border-brand-border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-brand-surface text-left text-xs uppercase tracking-wide text-brand-muted">
              <tr>
                <th className="px-3 py-2 font-semibold">Patient</th>
                <th className="px-3 py-2 font-semibold">Case</th>
                <th className="px-3 py-2 font-semibold">Lab</th>
                <th className="px-3 py-2 font-semibold">Sent</th>
                <th className="px-3 py-2 font-semibold">Due back</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {out_at_lab.map((c) => <OutRow key={c.id} c={c} />)}
            </tbody>
          </table>
        </div>
      )}

      {/* Recently returned / closed */}
      <h2 className="mb-2 text-sm font-semibold text-brand-ink">Recently returned</h2>
      {returned.length === 0 ? (
        <div className="rounded-xl border border-dashed border-brand-border bg-white px-6 py-8 text-center text-sm text-brand-muted">No cases returned in the last 30 days.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-brand-border bg-white">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-brand-border">
              {returned.map((c) => <ReturnedRow key={c.id} c={c} />)}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  )
}
